import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient, ORDER_STATUSES, statusColor, type RestaurantOrderStatus } from '@kss/api';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { RefreshCw, ClipboardList, ChevronDown, ChevronRight, Users, IndianRupee, UtensilsCrossed, ReceiptText } from 'lucide-react';

/**
 * Admin Restaurant Orders board (replaces the generic API-browser grid,
 * which dumped the line-item JSON into an unreadable `description` cell).
 *
 * Orders are grouped into TABLE-WISE cards. Each card shows the table's
 * order count, total amount and total item count; clicking it expands to
 * reveal every order at that table — order code, customer, amount, item
 * count, the itemised lines, and a status dropdown the admin can change.
 *
 * Item lines are read from the real `RestaurantOrderLine` table (keyed by
 * `restaurantOrderId`) and fall back to the `description` JSON envelope for
 * orders saved by the panel POS path before lines were split out.
 */

const POLL_MS = 15_000;
const inr = (n: unknown) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const pickList = (d: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
};

interface OrderItem { name: string; quantity: number; unitPrice?: number; lineTotal?: number; note?: string }
interface AdminOrder {
  id: string;
  code: string;
  customerName?: string;
  customerPhone?: string;
  tableId?: string;
  tableLabel: string;
  status: RestaurantOrderStatus;
  grandTotal: number;
  placedAt?: string;
  salesInvoiceId?: number | string | null;
  items: OrderItem[];
}
interface TableGroup { key: string; label: string; zone?: string; seats?: number; orders: AdminOrder[]; total: number; itemCount: number }

type Filter = 'open' | 'all' | 'closed';
const CLOSED: ReadonlySet<string> = new Set(['Billed', 'Paid', 'Cancelled', 'Voided']);

export default function RestaurantOrders() {
  const toast = useToast();
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [tableMap, setTableMap] = useState<Map<string, { label: string; zone?: string; seats?: number }>>(new Map());
  const [lineMap, setLineMap] = useState<Map<string, OrderItem[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('open');
  const [open, setOpen] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [orderRes, tableRes, lineRes] = await Promise.all([
        apiClient.get('/restaurant/RestaurantOrder'),
        apiClient.get('/restaurant/RestaurantTable').catch(() => ({ data: [] })),
        apiClient.get('/restaurant/RestaurantOrderLine').catch(() => ({ data: [] })),
      ]);

      const tMap = new Map<string, { label: string; zone?: string; seats?: number }>();
      for (const t of pickList(tableRes.data)) {
        const id = String(t.id ?? (t as { restaurantTableId?: unknown }).restaurantTableId ?? '');
        if (id) tMap.set(id, { label: String(t.name ?? t.code ?? `Table ${id}`), zone: t.zone ? String(t.zone) : undefined, seats: t.seatCount != null ? Number(t.seatCount) : undefined });
      }
      setTableMap(tMap);

      const lMap = new Map<string, OrderItem[]>();
      for (const l of pickList(lineRes.data)) {
        const oid = String(l.restaurantOrderId ?? '');
        if (!oid || oid === '0') continue;
        const item: OrderItem = {
          name: String(l.itemName ?? l.name ?? '—'),
          quantity: Number(l.quantity ?? 1) || 1,
          unitPrice: l.unitPrice != null ? Number(l.unitPrice) : undefined,
          lineTotal: l.lineTotal != null ? Number(l.lineTotal) : undefined,
          note: l.note ? String(l.note) : undefined,
        };
        (lMap.get(oid) ?? lMap.set(oid, []).get(oid)!).push(item);
      }
      setLineMap(lMap);
      setRows(pickList(orderRes.data));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const t = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(t);
  }, [refresh]);

  /** Items for an order: real line rows first, else the description JSON. */
  const itemsFor = useCallback((row: Record<string, unknown>): OrderItem[] => {
    const fromLines = lineMap.get(String(row.id ?? ''));
    if (fromLines?.length) return fromLines;
    try {
      const d = String(row.description ?? '');
      if (d.startsWith('{')) {
        const env = JSON.parse(d) as { items?: Array<Record<string, unknown>>; lines?: Array<Record<string, unknown>> };
        const src = env.lines?.length ? env.lines : env.items;
        if (Array.isArray(src)) return src.map((i) => ({
          name: String(i.name ?? i.itemName ?? '—'),
          quantity: Number(i.quantity ?? 1) || 1,
          unitPrice: i.unitPrice != null ? Number(i.unitPrice) : undefined,
          lineTotal: i.lineTotal != null ? Number(i.lineTotal) : undefined,
          note: i.note ? String(i.note) : undefined,
        }));
      }
    } catch { /* no envelope */ }
    return [];
  }, [lineMap]);

  const orders = useMemo<AdminOrder[]>(() => {
    const valid = (s: unknown): RestaurantOrderStatus =>
      (ORDER_STATUSES as readonly string[]).includes(String(s)) ? (String(s) as RestaurantOrderStatus) : 'Placed';
    const descCode = (row: Record<string, unknown>): string | undefined => {
      try { const d = String(row.description ?? ''); return d.startsWith('{') ? (JSON.parse(d) as { tableCode?: string }).tableCode : undefined; }
      catch { return undefined; }
    };
    const descCustomer = (row: Record<string, unknown>): string | undefined => {
      try { const d = String(row.description ?? ''); return d.startsWith('{') ? (JSON.parse(d) as { customerName?: string }).customerName : undefined; }
      catch { return undefined; }
    };
    return rows.map((row) => {
      const tableId = row.tableId != null && String(row.tableId) !== '0' ? String(row.tableId) : undefined;
      const label = (tableId && tableMap.get(tableId)?.label) || descCode(row) || (tableId ? `Table ${tableId}` : 'Unassigned · Takeaway');
      const items = itemsFor(row);
      return {
        id: String(row.id ?? ''),
        code: String(row.code ?? `#${row.id ?? ''}`),
        customerName: row.customerName ? String(row.customerName) : descCustomer(row),
        customerPhone: row.customerPhone ? String(row.customerPhone) : undefined,
        tableId,
        tableLabel: label,
        status: valid((row as { orderStatus?: unknown }).orderStatus ?? row.status),
        grandTotal: Number(row.grandTotal ?? (row as { total?: unknown }).total ?? 0),
        placedAt: row.placedAt ? String(row.placedAt) : (row.indate ? String(row.indate) : undefined),
        salesInvoiceId: (row as { salesInvoiceId?: number | string | null }).salesInvoiceId ?? null,
        items,
      };
    });
  }, [rows, tableMap, itemsFor]);

  const filtered = useMemo(
    () => orders.filter((o) => (filter === 'all' ? true : filter === 'closed' ? CLOSED.has(o.status) : !CLOSED.has(o.status))),
    [orders, filter],
  );

  const groups = useMemo<TableGroup[]>(() => {
    const m = new Map<string, TableGroup>();
    for (const o of filtered) {
      const key = o.tableId ?? o.tableLabel;
      let g = m.get(key);
      if (!g) { g = { key, label: o.tableLabel, zone: o.tableId ? tableMap.get(o.tableId)?.zone : undefined, seats: o.tableId ? tableMap.get(o.tableId)?.seats : undefined, orders: [], total: 0, itemCount: 0 }; m.set(key, g); }
      g.orders.push(o);
      g.total += o.grandTotal;
      g.itemCount += o.items.reduce((s, it) => s + it.quantity, 0);
    }
    // Most recently active table first; unassigned last.
    return [...m.values()].sort((a, b) => {
      if (a.label.startsWith('Unassigned') !== b.label.startsWith('Unassigned')) return a.label.startsWith('Unassigned') ? 1 : -1;
      const la = Math.max(0, ...a.orders.map((o) => Date.parse(o.placedAt ?? '') || 0));
      const lb = Math.max(0, ...b.orders.map((o) => Date.parse(o.placedAt ?? '') || 0));
      return lb - la;
    });
  }, [filtered, tableMap]);

  const totals = useMemo(() => ({
    orders: filtered.length,
    tables: groups.length,
    amount: filtered.reduce((s, o) => s + o.grandTotal, 0),
  }), [filtered, groups]);

  async function updateStatus(o: AdminOrder, value: string) {
    setRows((prev) => prev.map((r) => (String(r.id) === o.id ? { ...r, orderStatus: value } : r)));
    try {
      // Spread the FULL raw row — the RestaurantOrder PUT is a full-record
      // replace, so a minimal {id, code, status, orderStatus} body nulls
      // customerName / grandTotal / tableId / description (line JSON). Keep the
      // whole row and override only orderStatus.
      const raw = rows.find((r) => String(r.id) === o.id) ?? {};
      await apiClient.put('/restaurant/RestaurantOrder', { ...raw, id: Number(o.id), status: 0, orderStatus: value });
      toast.success('Status updated', `${o.code} → ${value}`);
    } catch (e) {
      toast.error('Update failed', e instanceof Error ? e.message : 'Try again');
      void refresh();
    }
  }

  const toggle = (key: string) => setOpen((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface-card px-4 py-3">
        <ClipboardList className="text-orange-500" size={22} />
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Restaurant Orders</h1>
          <p className="text-[11px] text-content-muted">
            {totals.orders} order(s) · {totals.tables} table(s) · ₹ {inr(totals.amount)} total
          </p>
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-border">
          {(['open', 'all', 'closed'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 text-xs capitalize ${filter === f ? 'bg-primary text-white' : 'bg-surface-card text-content-secondary'}`}
            >
              {f === 'open' ? 'Active' : f}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => void refresh()}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </header>

      {err && <Alert variant="danger" className="m-3">{err}</Alert>}

      <div className="flex-1 overflow-y-auto p-3">
        {loading && rows.length === 0 ? (
          <div className="grid place-items-center py-20 text-sm text-content-muted">Loading orders…</div>
        ) : groups.length === 0 ? (
          <div className="grid place-items-center py-20 text-center text-sm text-content-muted">
            <UtensilsCrossed className="mb-2 text-content-muted" size={28} />
            No {filter === 'open' ? 'active ' : filter === 'closed' ? 'closed ' : ''}orders. New orders from POS / dine-in appear here.
          </div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {groups.map((g) => (
              <TableGroupCard
                key={g.key}
                group={g}
                expanded={open.has(g.key)}
                onToggle={() => toggle(g.key)}
                onStatus={updateStatus}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TableGroupCard({ group, expanded, onToggle, onStatus }: {
  group: TableGroup;
  expanded: boolean;
  onToggle: () => void;
  onStatus: (o: AdminOrder, value: string) => void;
}) {
  const hottest = group.orders.find((o) => o.status === 'Cooking') ?? group.orders.find((o) => o.status === 'Ready') ?? group.orders[0];
  const c = hottest ? statusColor(hottest.status) : null;
  return (
    <Card padded={false} className="flex flex-col overflow-hidden">
      <button onClick={onToggle} className="flex items-center gap-2 px-3 py-2.5 text-left hover:bg-surface-card-hover">
        {expanded ? <ChevronDown size={16} className="text-content-muted" /> : <ChevronRight size={16} className="text-content-muted" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[15px] font-semibold text-content-primary">{group.label}</span>
            {c && <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: c.bg, color: c.fg }}>{c.label}</span>}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-content-muted">
            <span className="inline-flex items-center gap-1"><ReceiptText size={11} /> {group.orders.length} order(s)</span>
            <span className="inline-flex items-center gap-1"><UtensilsCrossed size={11} /> {group.itemCount} item(s)</span>
            {group.seats != null && <span className="inline-flex items-center gap-1"><Users size={11} /> {group.seats}</span>}
            {group.zone && <span>· {group.zone}</span>}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="inline-flex items-center text-[15px] font-semibold text-content-primary"><IndianRupee size={13} />{inr(group.total)}</div>
        </div>
      </button>

      {expanded && (
        <div className="space-y-2 border-t border-border bg-surface-app/40 p-2">
          {group.orders.map((o) => (
            <OrderDetail key={o.id} order={o} onStatus={onStatus} />
          ))}
        </div>
      )}
    </Card>
  );
}

function OrderDetail({ order, onStatus }: { order: AdminOrder; onStatus: (o: AdminOrder, value: string) => void }) {
  const itemCount = order.items.reduce((s, it) => s + it.quantity, 0);
  return (
    <div className="rounded-md border border-border bg-surface-card p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-content-primary">{order.code}</div>
          <div className="text-[11px] text-content-muted">
            {order.customerName || 'Walk-in'}{order.customerPhone ? ` · ${order.customerPhone}` : ''}
            {order.placedAt ? ` · ${fmt(order.placedAt)}` : ''}
            {order.salesInvoiceId ? ' · billed' : ''}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="inline-flex items-center text-[13px] font-semibold text-content-primary"><IndianRupee size={12} />{inr(order.grandTotal)}</div>
            <div className="text-[10px] text-content-muted">{itemCount} item(s)</div>
          </div>
          <select
            value={order.status}
            onChange={(e) => onStatus(order, e.target.value)}
            className="h-8 rounded-md border border-border bg-surface-card px-2 text-xs text-content-primary outline-none focus:border-primary"
            title="Change order status"
          >
            {(ORDER_STATUSES as readonly string[]).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {order.items.length > 0 ? (
        <table className="mt-2 w-full text-[12px]">
          <thead className="text-left text-[10px] uppercase tracking-wide text-content-muted">
            <tr><th className="py-1">Item</th><th className="py-1 text-center">Qty</th><th className="py-1 text-right">Rate</th><th className="py-1 text-right">Total</th></tr>
          </thead>
          <tbody>
            {order.items.map((it, i) => (
              <tr key={i} className="border-t border-border/60">
                <td className="py-1">
                  {it.name}
                  {it.note && <span className="ml-1 italic text-content-muted">“{it.note}”</span>}
                </td>
                <td className="py-1 text-center">{it.quantity}</td>
                <td className="py-1 text-right">{it.unitPrice != null ? `₹${inr(it.unitPrice)}` : '—'}</td>
                <td className="py-1 text-right">{it.lineTotal != null ? `₹${inr(it.lineTotal)}` : (it.unitPrice != null ? `₹${inr(it.unitPrice * it.quantity)}` : '—')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="mt-2 rounded bg-surface-app px-2 py-1.5 text-[11px] italic text-content-muted">
          No item lines captured for this order.
        </div>
      )}
    </div>
  );
}

function fmt(v?: string): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
